import React, { useEffect, useRef, useState } from 'react'
import { StarIcon } from '@heroicons/react/solid'
import useSWR from 'swr'
import Layout from './layout'

import { fetcher } from '@/components/youtube/utils/fetcher'

interface AggregatedTrendingRepo {
  name: string
  author: string
  description: string
  stars: string
  language: string
  url: string
  found: string[]
}

// Interface for a single repository
export interface TrendingRepo {
  name: string
  author: string
  description: string
  stars: string
  language: string
  url: string
  found: string[] // Represents periods like "daily", "weekly", "monthly"
}

// Interface for the entire data structure
export type TrendingData = Record<string, TrendingRepo[]>

function TrendingRepos() {
  const [apiUrl] = useState('https://ilya1.com/api')

  const { data: trendingMap = {}, error } = useSWR<TrendingData>(`${apiUrl}/trending`, fetcher)
  const [languages, setLanguages] = useState<string[]>([])
  const languageRefs = useRef<Record<string, HTMLDivElement>>({})

  useEffect(() => {
    if (trendingMap && Object.keys(trendingMap).length) {
      setLanguages(Object.keys(trendingMap))
    }
  }, [trendingMap])

  const scrollToLanguage = (language: string) => {
    const element = languageRefs.current[language]
    if (element) {
      const offset = 60 // Adjust scroll offset
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  if (error) {
    return <div className="text-red-500">Failed to load trending repositories</div>
  }

  if (!Object.keys(trendingMap).length) {
    return <div className="text-gray-5">Loading trending repositories...</div>
  }

  return (
    <div className="m-1 prose">
      {/* Sticky Bar */}
      <div className="sticky top-0 bg-white z-10 p-0">
        <div className="flex flex-wrap gap-2 ">
          <button
            key="home"
            className="px-3 py-1 bg-gray-2 hover:bg-gray-3 rounded text-sm capitalize flex items-center"
            onClick={() => (window.location.href = '/')}
          >
            <span className="mr-2">🏠</span>
          </button>{' '}
          <span style={{ marginLeft: 10 }}> </span>
          {languages.map((language) => (
            <button
              key={language}
              className="px-3 py-1 bg-gray-2 hover:bg-gray-3 rounded text-sm capitalize"
              onClick={() => scrollToLanguage(language)}
            >
              {language}
            </button>
          ))}
        </div>
      </div>

      <h1 className="text-2xl font-bold mb-4 mt-2">Github Trending Repositories</h1>

      {Object.entries(trendingMap).map(([language, repos]) => (
        <div key={language} className="mb-4" ref={(el) => (languageRefs.current[language] = el!)}>
          <h2 className="text-xl font-semibold mb-2 capitalize">{language}</h2>
          <div className="grid gap-1 sm:grid-cols-3 md:grid-cols-4">
            {repos.map((repo) => (
              <div key={`${repo.author}/${repo.name}`} className="border rounded p-2 bg-white flex flex-col">
                <a
                  href={repo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 font-bold hover:underline break-words"
                  title={repo.description}
                >
                  {repo.author}/{repo.name}
                </a>
                <p
                  className="text-sm text-gray-6 mt-1 md:truncate md:overflow-hidden md:whitespace-nowrap"
                  title={repo.description}
                >
                  {repo.description.length > 100 ? `${repo.description.slice(0, 100)}...` : repo.description}
                </p>
                <div className="mt-auto pt-1">
                  <div className="text-sm text-gray-7">
                    <span
                      key={language}
                      className="inline-block px-1 py-1 mt-1 mb-1 mr-1 bg-gray-3 rounded text-xs cursor-pointer"
                      onClick={() => {
                        window.open(`https://github.com/trending/${language}`, '_blank')
                      }}
                    >
                      {language}
                    </span>

                    {repo.stars}
                    <div style={{ width: 12, height: 12, display: 'inline-block' }}>
                      <StarIcon />
                    </div>
                    {repo.found.map((period) => (
                      <span
                        key={period}
                        className="inline-block px-1 py-1 m-1 bg-gray-1 rounded text-xs cursor-pointer"
                        onClick={() => {
                          window.open(`https://github.com/trending/${language}?since=${period}`, '_blank')
                        }}
                      >
                        {period}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function Trending() {
  return (
    <Layout>
      <section className="boxes-container">
        <TrendingRepos />
      </section>
    </Layout>
  )
}
